// Orientation-lock ("frozen position") detection for rendered I2V prompts.
//
// The bundle-v5 orientation lock (orchestrator plan 2026-07-23) steers the
// prompt director to hold each prominent subject's captured facing direction so
// facing-away subjects never turn toward the camera. A scene "triggered" the
// lock when its effective prompt carries such an anchor.
//
// These patterns MIRROR the orchestrator's production heuristic in
// echoflicks-ml-orchestrator/services/i2v_authoring/orientation.py (the source
// of truth, shared there with the dev probe and the I2V_SCENE_AUTHORED log
// line). Keep the two lists in sync when either side evolves.
//
// Heuristic, not a validator: used for badges/telemetry only — a miss must
// never gate anything.

const ORIENTATION_ANCHOR_PATTERNS: RegExp[] = [
  /\b(?:back|backs) to (?:the )?(?:camera|viewer)\b/,
  /\bbacks? (?:stay|stays|remain|remains|keeps?|fully turned|to)\b/,
  /\bkeeps? (?:her|his|their|its) (?:full )?back\b/,
  /\b(?:stay|stays|remain|remains|keep|keeps|hold|holds|maintain|maintains)\b[^.;]{0,48}\bprofile\b/,
  /\bprofile\b[^.;]{0,36}\b(?:stay|stays|remain|remains|keep|keeps|hold|holds|maintain|maintains)\b/,
  /\bfacing away\b/,
  /\b(?:stay|stays|remain|remains|keep|keeps)\b[^.;]{0,24}\bfacing\b/,
  /\bseen from behind\b[^.]{0,160}\b(?:stay|stays|keep|keeps) facing\b/,
];

/**
 * Return the first orientation-anchor phrase found in the prompt, else null.
 * Matching is case-insensitive; the returned phrase is the lowercased match,
 * intended for badge tooltips.
 */
export function detectOrientationAnchor(
  prompt: string | null | undefined,
): string | null {
  if (!prompt) return null;
  const lowered = prompt.toLowerCase();
  for (const pattern of ORIENTATION_ANCHOR_PATTERNS) {
    const match = pattern.exec(lowered);
    if (match) return match[0];
  }
  return null;
}

/**
 * Map scene index -> the anchor phrase for every scene whose rendered prompt
 * locked orientation. Typically only a handful of an echo's scenes qualify, so
 * the Director View uses this to mark them on the scene strip (otherwise the
 * per-scene badge is only visible once you happen to land on the right scene).
 */
export function buildOrientationLockIndex(
  scenes: readonly { index: number; sceneId: number | string | null }[],
  auditFor: (scene: {
    index: number;
    sceneId: number | string | null;
  }) => { finalPositivePrompt?: string | null } | null | undefined,
): Map<number, string> {
  const locked = new Map<number, string>();
  for (const scene of scenes) {
    const anchor = detectOrientationAnchor(
      auditFor(scene)?.finalPositivePrompt,
    );
    if (anchor) locked.set(scene.index, anchor);
  }
  return locked;
}
